import { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendUp } from "@phosphor-icons/react";
import api from "../lib/api";
import { fmtCurrency, fmtNumber, fmtDecimal, fmtDateShort, hasPerm } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const RANGES = [
  { key: "last_7", label: "Last 7 Days" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "this_month", label: "This Month" },
  { key: "previous_month", label: "Previous Month" },
  { key: "custom", label: "Custom" },
];

const METRICS = {
  net_sales: { label: "Net Sales", color: "#1d4ed8", fmt: (v) => fmtCurrency(v), axis: (v) => `₹${Math.round(v / 1000)}k` },
  bills: { label: "Number of Bills", color: "#7c3aed", fmt: (v) => fmtNumber(v), axis: (v) => fmtNumber(v) },
  abv: { label: "ABV", color: "#0d9488", fmt: (v) => fmtCurrency(v, true), axis: (v) => `₹${Math.round(v / 1000)}k` },
  abq: { label: "ABQ", color: "#ea580c", fmt: (v) => fmtDecimal(v), axis: (v) => fmtDecimal(v, 0) },
};

function TooltipBox({ active, payload, label, context, metricKey }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const m = METRICS[metricKey];
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg" data-testid="trend-tooltip">
      <div className="font-semibold text-slate-800">{fmtDateShort(label)}</div>
      <div className="mt-0.5 text-slate-500">{context}</div>
      <div className="mt-1 text-slate-700">{m.label}: <b>{m.fmt(p[metricKey])}</b></div>
      <div className="text-slate-400">Bills: {p.bills}</div>
    </div>
  );
}

export default function TrendCharts() {
  const { user } = useAuth();
  const showAmount = hasPerm(user, "view_sales_amount");
  const [metric, setMetric] = useState("net_sales");
  const [scope, setScope] = useState("overall");
  const [scopeValue, setScopeValue] = useState("");
  const [range, setRange] = useState("this_month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [floors, setFloors] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [cats, setCats] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/floors").then((r) => setFloors(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/dashboard/salesmen", { params: { period: "this_month" } })
      .then((r) => setSalesmen(r.data.salesmen.map((s) => s.name))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (range === "custom" && (!custom.start || !custom.end)) return;
    if (scope !== "overall" && !scopeValue) { setSeries([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { period: range, scope };
      if (scope !== "overall") params.scope_value = scopeValue;
      if (range === "custom") { params.start = custom.start; params.end = custom.end; }
      const { data } = await api.get("/dashboard/trend", { params });
      setSeries(data.series);
    } finally {
      setLoading(false);
    }
  }, [scope, scopeValue, range, custom]);

  useEffect(() => { load(); }, [load]);

  const options = scope === "floor" ? floors : scope === "salesman" ? salesmen : cats;
  const context = scope === "overall" ? "Overall Store" : `${scope[0].toUpperCase() + scope.slice(1)}: ${scopeValue || "—"}`;
  const m = METRICS[metric];
  const data = loading ? [] : series;
  const metricList = showAmount ? Object.keys(METRICS) : ["bills", "abv", "abq"];

  return (
    <section className="space-y-3" data-testid="trend-section">
      <div className="flex items-center gap-2">
        <TrendUp size={20} weight="bold" className="text-blue-700" />
        <h2 className="font-display text-lg font-bold text-slate-800">Trends</h2>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="trend-metric" value={metric} onChange={(e) => setMetric(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700">
          {metricList.map((k) => <option key={k} value={k}>{METRICS[k].label}</option>)}
        </select>
        <select data-testid="trend-scope" value={scope} onChange={(e) => { setScope(e.target.value); setScopeValue(""); }}
          className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700">
          <option value="overall">Overall Store</option>
          <option value="floor">Floor</option>
          <option value="category">Category</option>
          <option value="salesman">Salesman</option>
        </select>
        {scope !== "overall" && (
          <select data-testid="trend-scope-value" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700">
            <option value="">Select {scope}…</option>
            {options.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        <select data-testid="trend-range" value={range} onChange={(e) => setRange(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700">
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {range === "custom" && (
          <>
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="trend-start" />
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="trend-end" />
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="trend-chart">
        <div className="mb-3 text-sm font-semibold text-slate-700">{m.label} Trend · {context}</div>
        {data.length === 0 ? (
          <div className="flex h-56 items-center justify-center text-sm text-slate-400">{scope !== "overall" && !scopeValue ? `Select a ${scope} to view its trend` : "No data for this range"}</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 5, right: 10, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => fmtDateShort(d).slice(0, 6)} tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={m.axis} width={52} />
              <Tooltip content={<TooltipBox context={context} metricKey={metric} />} />
              <Line type="monotone" dataKey={metric} stroke={m.color} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
